import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Check, Star, Users, MessageSquare, Shield, Zap, RefreshCw, Settings } from "lucide-react";
import { useState, useEffect } from "react";

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: string;
  features: string[];
  listingLimit: number;
  popular?: boolean;
  stripeId?: string;
}

export default function SubscriptionPage() {
  const { user, subscription, subscriptionLoading, refreshSubscription } = useAuth();
  const { toast } = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const plans: SubscriptionPlan[] = [
    {
      id: "free",
      name: "Free",
      price: 0,
      interval: "forever",
      features: [
        "Browse all listings",
        "Submit unlimited referrals",
        "Basic search filters",
        "Email notifications"
      ],
      listingLimit: 0
    },
    {
      id: "tier1",
      name: "Tier 1",
      price: 100,
      interval: "month",
      features: [
        "1 location maximum",
        "Receive and manage referrals",
        "Basic analytics dashboard",
        "Email support",
        "Profile verification badge"
      ],
      listingLimit: 1,
      stripeId: "tier1"
    },
    {
      id: "tier2",
      name: "Tier 2", 
      price: 150,
      interval: "month",
      features: [
        "2 locations maximum",
        "All Tier 1 features included",
        "Priority email support",
        "Enhanced analytics & reporting",
        "Custom facility branding"
      ],
      popular: true,
      listingLimit: 2,
      stripeId: "tier2"
    },
    {
      id: "tier3",
      name: "Tier 3",
      price: 225,
      interval: "month",
      features: [
        "3 locations included",
        "Unlimited additional locations (+$50/month each)",
        "All Tier 2 features included",
        "Premium placement in search results",
        "Dedicated account manager",
        "Custom integration support",
        "Advanced reporting dashboard"
      ],
      listingLimit: 3,
      stripeId: "tier3"
    }
  ];

  const getCurrentPlan = () => {
    if (!subscription) return null;
    return plans.find(plan => 
      plan.name.toLowerCase().replace(' ', '_') === subscription.subscription_tier?.toLowerCase()
    );
  };

  const handleSubscribe = async (planId: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to subscribe to a plan.",
        variant: "destructive",
      });
      return;
    }

    // Handle free plan activation
    if (planId === "free") {
      try {
        const { error } = await supabase
          .from("profiles")
          .update({ subscription_status: "active" })
          .eq("user_id", user.id);

        if (error) throw error;

        toast({
          title: "Plan activated!",
          description: "Your free plan has been activated successfully.",
        });
        
        // Refresh subscription data
        setTimeout(() => {
          refreshSubscription();
        }, 1000);
      } catch (error) {
        console.error("Error activating free plan:", error);
        toast({
          title: "Error",
          description: "Failed to activate free plan. Please try again.",
          variant: "destructive",
        });
      }
      return;
    }

    // Handle paid plan checkout
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { tier: planId },
      });

      if (error) throw error;

      if (data?.url) {
        // Open Stripe checkout in a new tab
        window.open(data.url, '_blank');
        
        toast({
          title: "Redirecting to Stripe",
          description: "You'll be redirected to complete your subscription. This will open in a new tab.",
        });
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast({
        title: "Error",
        description: "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleManageSubscription = async () => {
    if (!user || !subscription?.subscribed) return;

    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
        toast({
          title: "Redirecting to Stripe",
          description: "You'll be redirected to manage your subscription. This will open in a new tab.",
        });
      }
    } catch (error) {
      console.error("Error creating portal session:", error);
      toast({
        title: "Error",
        description: "Failed to open subscription management. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRefreshSubscription = async () => {
    setIsRefreshing(true);
    try {
      await refreshSubscription();
      toast({
        title: "Subscription refreshed",
        description: "Your subscription status has been updated.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh subscription status.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    // Check for success/cancel parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (success === 'true') {
      toast({
        title: "Subscription successful!",
        description: "Welcome to your new plan. Your subscription is now active.",
      });
      // Refresh subscription data after successful checkout
      setTimeout(() => {
        refreshSubscription();
      }, 2000);
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled === 'true') {
      toast({
        title: "Checkout canceled",
        description: "You can complete your subscription anytime.",
        variant: "destructive",
      });
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast, refreshSubscription]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <div className="container mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-4xl font-bold">Choose Your Plan</h1>
          <Button
            onClick={handleRefreshSubscription}
            disabled={isRefreshing || subscriptionLoading}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh Status
          </Button>
        </div>
        <p className="text-xl text-center text-muted-foreground mb-8">
          Monthly recurring billing until canceled • Cancel anytime through your account settings
        </p>

        {/* Current subscription status */}
        {subscription && (
          <Alert className="mb-8">
            <AlertDescription className="flex items-center justify-between">
              <div>
                <strong>Current Plan:</strong>{" "}
                {subscription.subscribed && subscription.subscription_tier 
                  ? `${subscription.subscription_tier} Plan` 
                  : "Free Plan"
                }
                {subscription.subscription_end && (
                  <span className="ml-2 text-sm text-muted-foreground">
                    (Next billing: {new Date(subscription.subscription_end).toLocaleDateString()})
                  </span>
                )}
                {subscription.dashboard_access_suspended && (
                  <Badge variant="destructive" className="ml-2">Payment Issue</Badge>
                )}
              </div>
              {subscription.subscribed && (
                <Button
                  onClick={handleManageSubscription}
                  variant="outline"
                  size="sm"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Manage Subscription
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {subscriptionLoading && (
          <Alert className="mb-8">
            <AlertDescription>
              Loading subscription status...
            </AlertDescription>
          </Alert>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {plans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`relative ${plan.popular ? 'border-primary shadow-lg scale-105' : ''} ${
                subscription?.subscribed && getCurrentPlan()?.id === plan.id ? 'ring-2 ring-primary' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground">
                    <Star className="w-3 h-3 mr-1" />
                    Most Popular
                  </Badge>
                </div>
              )}
              
              {subscription?.subscribed && getCurrentPlan()?.id === plan.id && (
                <div className="absolute -top-3 right-4">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <Check className="w-3 h-3 mr-1" />
                    Your Plan
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-primary">
                    ${plan.price}
                  </span>
                  <span className="text-muted-foreground">/{plan.interval}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {plan.listingLimit === 0 ? "Browse listings only" : `Up to ${plan.listingLimit} facility listings`}
                </p>
              </CardHeader>

              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  className="w-full"
                  variant={plan.popular ? "default" : "outline"}
                  disabled={subscription?.subscribed && getCurrentPlan()?.id === plan.id}
                >
                  {subscription?.subscribed && getCurrentPlan()?.id === plan.id 
                    ? "Current Plan" 
                    : plan.id === "free" 
                      ? "Activate Free Plan" 
                      : "Subscribe Now"
                  }
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Features Comparison */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-center">Why Choose CareKonnect?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <Users className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Statewide Network</h3>
                <p className="text-muted-foreground text-sm">
                  Connect with care providers and case managers across all 87 Minnesota counties.
                </p>
              </div>
              <div className="text-center">
                <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Secure & Compliant</h3>
                <p className="text-muted-foreground text-sm">
                  Email-only referral system that keeps sensitive information in your secure email environment.
                </p>
              </div>
              <div className="text-center">
                <Zap className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Real-time Updates</h3>
                <p className="text-muted-foreground text-sm">
                  Instant notifications and real-time bed availability updates for faster placements.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Important Notice about Recurring Billing */}
        <Alert className="mb-8">
          <MessageSquare className="h-4 w-4" />
          <AlertDescription>
            <strong>Important:</strong> All paid subscriptions are billed monthly on a recurring basis until canceled. 
            You can cancel your subscription anytime through the "Manage Subscription" button above or by contacting support. 
            Your access will continue until the end of your current billing period.
          </AlertDescription>
        </Alert>

        {/* Support Information */}
        <Card>
          <CardContent className="text-center py-8">
            <h3 className="text-lg font-semibold mb-4">Need Help?</h3>
            <p className="text-muted-foreground mb-6">
              Our team is here to help you find the right plan for your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="outline">
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}